import { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Package, HardHat, Flag } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  delivery: { color: "bg-[#F5EDD3] text-[#5E4915] border-[#E8DDBF]", bar: "bg-[#F5F1E6]0", icon: Package },
  subcontractor: { color: "bg-sky-100 text-sky-800 border-sky-200", bar: "bg-sky-500", icon: HardHat },
  milestone: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", bar: "bg-emerald-500", icon: Flag },
};

const DAYS = 42; // 6 weeks

function dateOnly(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function diffDays(a, b) {
  return Math.round((dateOnly(b) - dateOnly(a)) / (1000 * 60 * 60 * 24));
}

export default function TimelinePage() {
  const { t, lang } = useLang();
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    project_id: "", title: "", type: "delivery",
    start_date: "", end_date: "", contractor: "", notes: "", status: "scheduled",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [ev, pr] = await Promise.all([api.get(`/timeline?lang=${lang}`), api.get(`/projects?lang=${lang}`)]);
        if (cancelled) return;
        setEvents(ev.data);
        setProjects(pr.data);
        if (pr.data[0]) setForm((f) => (f.project_id ? f : { ...f, project_id: pr.data[0].id }));
      } catch (err) {
        if (!cancelled) toast.error(apiErr(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lang]);

  const reload = async () => {
    try {
      const [ev, pr] = await Promise.all([api.get(`/timeline?lang=${lang}`), api.get(`/projects?lang=${lang}`)]);
      setEvents(ev.data); setProjects(pr.data);
    } catch (err) { toast.error(apiErr(err)); }
  };

  const today = dateOnly(new Date());
  const days = useMemo(() => Array.from({ length: DAYS }, (_, i) => {
    const d = new Date(today); d.setDate(d.getDate() + i); return d;
  }), [today]);

  const filtered = events.filter((e) => projectFilter === "all" || e.project_id === projectFilter);

  const save = async () => {
    if (!form.project_id || !form.title || !form.start_date) {
      toast.error(t("fill_event_title_project_date")); return;
    }
    try {
      await api.post("/timeline", {
        ...form, end_date: form.end_date || form.start_date,
      });
      setDialogOpen(false); await reload();
      toast.success(t("event_added"));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="timeline-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-eyebrow">{t("weeks_ahead")}</p>
          <h1 className="font-[Fraunces] text-4xl font-bold tracking-tight text-neutral-950 mt-1">{t("timeline")}</h1>
        </div>
        <div className="flex items-center gap-3">
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-56" data-testid="timeline-project-filter">
              <SelectValue placeholder={t("filter_project")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#B8933A] hover:bg-[#8C6D22]" data-testid="new-event-button">
                <Plus className="w-4 h-4 mr-1" />{t("new_event")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-[Fraunces]">{t("new_event")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label className="label-eyebrow">{t("event_title")}</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title-input" />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label className="label-eyebrow">{t("projects")}</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger data-testid="event-project-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-eyebrow">{t("type")}</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="delivery">{t("delivery")}</SelectItem>
                      <SelectItem value="subcontractor">{t("subcontractor")}</SelectItem>
                      <SelectItem value="milestone">{t("milestone")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-eyebrow">{t("contractor")}</Label>
                  <Input value={form.contractor} onChange={(e) => setForm({ ...form, contractor: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label className="label-eyebrow">{t("start_date")}</Label>
                  <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="event-start-input" />
                </div>
                <div className="space-y-2">
                  <Label className="label-eyebrow">{t("end_date")}</Label>
                  <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label className="label-eyebrow">{t("notes")}</Label>
                  <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("cancel")}</Button>
                <Button className="bg-[#B8933A] hover:bg-[#8C6D22]" onClick={save} data-testid="save-event-button">{t("save")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Gantt */}
      <div className="surface-card overflow-x-auto">
        <div className="min-w-[900px]">
          {/* Header row: dates */}
          <div className="grid" style={{ gridTemplateColumns: `220px repeat(${DAYS}, minmax(28px, 1fr))` }}>
            <div className="px-4 py-3 border-b border-neutral-200 label-eyebrow">{t("timeline_events")}</div>
            {days.map((d, i) => {
              const isToday = d.getTime() === today.getTime();
              const isMonday = d.getDay() === 1;
              return (
                <div key={i}
                  className={`text-[10px] text-center py-3 border-b border-neutral-200 ${isToday ? "bg-[#F5F1E6] text-[#B8933A] font-bold" : "text-neutral-500"} ${isMonday ? "border-l border-neutral-200" : ""}`}>
                  <div>{d.getDate()}</div>
                  {(i === 0 || d.getDate() === 1) && (
                    <div className="text-[9px] uppercase tracking-wider">{d.toLocaleString(undefined, { month: "short" })}</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Event rows */}
          {loading ? (
            <div className="p-8 text-center text-sm text-neutral-400" data-testid="timeline-loading">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-neutral-400" data-testid="timeline-empty">{t("no_events")}</div>
          ) : (
            filtered.map((e) => {
              const start = dateOnly(e.start_date);
              const end = dateOnly(e.end_date || e.start_date);
              const offset = Math.max(0, diffDays(today, start));
              const length = Math.max(1, diffDays(start, end) + 1);
              if (offset >= DAYS) return null;
              const meta = TYPE_META[e.type] || TYPE_META.delivery;
              const Icon = meta.icon;
              return (
                <div key={e.id}
                  data-testid={`timeline-row-${e.id}`}
                  className="grid items-center hover:bg-neutral-50 transition-colors duration-200"
                  style={{ gridTemplateColumns: `220px repeat(${DAYS}, minmax(28px, 1fr))` }}>
                  <div className="px-4 py-3 border-b border-neutral-100">
                    <div className="text-sm font-medium text-neutral-950 truncate">{e.title}</div>
                    <div className="text-[11px] text-neutral-500 truncate">{projectName(e.project_id)}</div>
                  </div>
                  <div className="border-b border-neutral-100 col-span-full relative h-14"
                    style={{ gridColumn: `2 / span ${DAYS}` }}>
                    <div
                      className={`absolute top-1/2 -translate-y-1/2 h-7 rounded-full flex items-center gap-1.5 px-2.5 text-white text-xs font-medium shadow-sm ${meta.bar}`}
                      style={{ left: `calc(${offset} * (100% / ${DAYS}))`, width: `calc(${length} * (100% / ${DAYS}) - 4px)`, minWidth: "40px" }}
                      title={`${e.title} — ${e.contractor || ""}`}
                    >
                      <Icon className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{e.contractor || t(e.type)}</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-neutral-600">
        {Object.entries(TYPE_META).map(([k, m]) => (
          <div key={k} className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${m.bar}`} />{t(k)}
          </div>
        ))}
      </div>
    </div>
  );
}
